import React, { useEffect, useState } from 'react'
import { IBaseLoyaltyTier } from '@getgreenline/homi-shared'
import { observer } from 'mobx-react-lite'
import { Popconfirm, Radio } from 'antd-v4'
import { CollapseProps } from 'antd/lib/collapse'
import { SingleCollapsibleContainer } from '../../../../../components/ResponsiveContainer/SingleCollapsibleContainer'
import { ResponsiveRow } from '../../../../../components/ResponsiveContainer/ResponsiveRow'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { inject } from 'mobx-react'
import { Select } from 'antd'
import { THEME_COLORS } from '@getgreenline/shared'
import { BlazeInputNumber } from '../../../../../components/Input/BlazeInputNumber'
import { DiscountModels } from '@getgreenline/products'
import { Fade } from 'react-reveal'
import { getPopupContainer } from '../../../../../utilities/helpers'
import { ICreateDiscountLoyaltyTier } from '@getgreenline/products/build/models/discount'
import { isEmpty, sortBy } from 'lodash'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface Props {
  discount: DiscountModels.CreateOrEditDiscount
  loyaltyTiers: IBaseLoyaltyTier[]
  collapseProps: CollapseProps
  defaultFadeAnimationProps: ReactRevealProps
  enableLoyaltyTargeting: boolean
  setEnableLoyaltyTargeting: (value: boolean) => void
  updateDiscountLoyaltyCost: (value?: string | number) => void
}

const _AddDiscountLoyalty: React.FC<InjectedProps & Props> = ({
  discount,
  collapseProps,
  loyaltyTiers,
  defaultFadeAnimationProps,
  updateDiscountLoyaltyCost,
  currentCompanyStore,
  enableLoyaltyTargeting,
  setEnableLoyaltyTargeting,
}) => {
  const [selectedLoyaltyTiers, setSelectedLoyaltyTiers] = useState<number[]>(() => {
    if (!isEmpty(discount.loyaltyTiers)) {
      return discount.loyaltyTiers!.map((l) => l.id)
    }

    if (discount.loyaltyTierId) {
      return [discount.loyaltyTierId]
    }

    return []
  })

  const hasMaximumLoyaltyTiers = selectedLoyaltyTiers.length === 5

  const shouldDisableLoyaltyTierOption = (loyaltyTierId: number) =>
    hasMaximumLoyaltyTiers && !selectedLoyaltyTiers.includes(loyaltyTierId)

  const shouldDisableAutoLoyaltyDiscount =
    !currentCompanyStore?.canUseAutoLoyaltyDiscount && discount.automatic

  const shouldDisableMultipleLoyaltyTiers = !currentCompanyStore?.canUseDiscountMultipleLoyaltyTiers

  const resetLoyaltySettings = () => {
    discount.setLoyaltyCost(0)
    setSelectedLoyaltyTiers([])
    setEnableLoyaltyTargeting(false)
  }

  useEffect(() => {
    setEnableLoyaltyTargeting(isEmpty(discount.loyaltyTiers) ? !!discount.loyaltyTierId : true)
  }, [])

  useEffect(() => {
    if (isEmpty(selectedLoyaltyTiers)) {
      discount.setIsLoyaltyDiscount(false)
      discount.setLoyaltyTierId(null)
      discount.setLoyaltyTiers([])
      return
    }

    const updatedLoyaltyTiers: ICreateDiscountLoyaltyTier[] = selectedLoyaltyTiers.map(
      (loyaltyTierId) => {
        return { id: loyaltyTierId }
      },
    )

    discount.setIsLoyaltyDiscount(true)
    discount.setLoyaltyTierId(updatedLoyaltyTiers[0].id)
    discount.setLoyaltyTiers(updatedLoyaltyTiers)
  }, [selectedLoyaltyTiers])

  return (
    <SingleCollapsibleContainer
      collapseProps={collapseProps}
      panelProps={{ header: 'Loyalty', key: 'loyalty' }}
      defaultActive
    >
      <ResponsiveRow labelProps={{ className: 'mt-0' }} labelText={<strong>Loyalty tier</strong>}>
        <Radio.Group value={enableLoyaltyTargeting ? 'specified' : 'all'}>
          {!isEmpty(selectedLoyaltyTiers) ? (
            <Popconfirm
              title='This will clear the selected loyalty tier'
              onConfirm={() => resetLoyaltySettings()}
            >
              <Radio
                value='all'
                disabled={shouldDisableAutoLoyaltyDiscount}
                style={{ display: 'block', marginBottom: 4 }}
              >
                Not required
              </Radio>
            </Popconfirm>
          ) : (
            <Radio
              value='all'
              disabled={shouldDisableAutoLoyaltyDiscount}
              style={{ display: 'block', marginBottom: 4 }}
              onClick={() => resetLoyaltySettings()}
            >
              Not required
            </Radio>
          )}

          <Radio
            value='specified'
            onClick={() => setEnableLoyaltyTargeting(true)}
            style={{ display: 'block', marginBottom: 4 }}
            disabled={shouldDisableAutoLoyaltyDiscount}
          >
            Specified
          </Radio>
        </Radio.Group>

        <div className='caption mb-3'>
          Target this discount to only specified customers in their respective loyalty tiers
          {shouldDisableAutoLoyaltyDiscount && (
            <>
              <br />
              <span style={{ color: THEME_COLORS.ACTIONS.dangerAlt, marginBottom: 0 }}>
                (Loyalty is disabled when automatic discount is on)
              </span>
            </>
          )}
        </div>

        {enableLoyaltyTargeting && (
          <Fade {...defaultFadeAnimationProps}>
            <div className='mb-3'>
              <div className='caption mb-3'>
                <strong>Loyalty tier</strong>
              </div>
              {shouldDisableMultipleLoyaltyTiers ? (
                <Select<number>
                  allowClear
                  mode='default'
                  showArrow={true}
                  placeholder={isEmpty(selectedLoyaltyTiers) && 'Select a loyalty tier'}
                  defaultValue={selectedLoyaltyTiers[0]}
                  onChange={(value) =>
                    value ? setSelectedLoyaltyTiers([value]) : setSelectedLoyaltyTiers([])
                  }
                  disabled={shouldDisableAutoLoyaltyDiscount}
                  getPopupContainer={getPopupContainer}
                  style={{ width: 200 }}
                  listHeight={150}
                >
                  {loyaltyTiers.map((loyaltyTier) => (
                    <Select.Option
                      key={loyaltyTier.id}
                      value={loyaltyTier.id}
                      label={loyaltyTier.name}
                      disabled={shouldDisableLoyaltyTierOption(loyaltyTier.id)}
                    >
                      {loyaltyTier.name}
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                <>
                  <Select<number[]>
                    allowClear
                    mode='multiple'
                    showArrow={true}
                    placeholder={isEmpty(selectedLoyaltyTiers) && 'Select a loyalty tier'}
                    value={selectedLoyaltyTiers}
                    onChange={(values) => setSelectedLoyaltyTiers(sortBy(values))}
                    disabled={shouldDisableAutoLoyaltyDiscount}
                    optionFilterProp='children'
                    getPopupContainer={getPopupContainer}
                    style={{ width: '100%' }}
                    listHeight={150}
                  >
                    {loyaltyTiers.map((loyaltyTier) => (
                      <Select.Option
                        key={loyaltyTier.id}
                        value={loyaltyTier.id}
                        label={loyaltyTier.name}
                        disabled={shouldDisableLoyaltyTierOption(loyaltyTier.id)}
                      >
                        {loyaltyTier.name}
                      </Select.Option>
                    ))}
                  </Select>
                  {!isEmpty(selectedLoyaltyTiers) && (
                    <div className='caption'>
                      {`${selectedLoyaltyTiers.length} loyalty tier${
                        selectedLoyaltyTiers.length > 1 ? 's are' : ' is'
                      } selected`}
                      {hasMaximumLoyaltyTiers && (
                        <span style={{ color: THEME_COLORS.ACTIONS.dangerAlt, display: 'block' }}>
                          (Maximum loyalty tiers per discount reached)
                        </span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </Fade>
        )}
      </ResponsiveRow>
      <ResponsiveRow labelText={<strong>Loyalty points</strong>}>
        Loyalty points required for redemption
        <BlazeInputNumber
          style={{ width: '100px', marginLeft: '10px' }}
          min={0}
          value={discount.loyaltyCost}
          isEditable
          removeDecimalPoints
          disabled={discount.automatic}
          onChange={updateDiscountLoyaltyCost}
        />
        <>
          {discount.automatic && (
            <div className='mb-3 mt-1'>
              <span
                style={{
                  color: THEME_COLORS.ACTIONS.dangerAlt,
                  marginBottom: 0,
                  fontSize: '12px',
                }}
              >
                (Loyalty points are disabled when automatic discount is on)
              </span>
            </div>
          )}
        </>
      </ResponsiveRow>
    </SingleCollapsibleContainer>
  )
}

export const AddDiscountLoyalty = inject('currentCompanyStore')(observer(_AddDiscountLoyalty))
